<template>
  <Teleport :to="teleport">
    <div
      v-show="show"
      ref="rootRef"
      :class="['floating_bubble', attrs.class]"
      :style="[rootStyle, inheritedStyle]"
      @touchstart.passive="onTouchStart"
      @touchend="onTouchEnd"
      @touchcancel="onTouchEnd"
      @click.capture="onClick"
      @mousedown="onMouseDown"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
      @mouseleave="onMouseUp">
      <div>
        <slot />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useAttrs, watch, type CSSProperties } from 'vue'
import { useWindowSize } from '@vueuse/core'
import { isObject } from 'lodash-es'
import { addUnit, closest, makeStringProp, pick } from './const'
import type {
  FloatingBubbleAxis,
  FloatingBubbleBoundary,
  FloatingBubbleGap,
  FloatingBubbleMagnetic,
  FloatingBubbleOffset
} from './types'
import './index.scss'

defineOptions({
  inheritAttrs: false
})

interface Props {
  gap?: FloatingBubbleGap
  icon?: string
  axis?: FloatingBubbleAxis
  magnetic?: FloatingBubbleMagnetic
  offset?: FloatingBubbleOffset
  teleport?: string | Element
}

const props = withDefaults(defineProps<Props>(), {
  gap: 24,
  axis: 'y',
  teleport: 'body'
})
const attrs = useAttrs()
const inheritedStyle = computed(() => attrs.style as CSSProperties | string | undefined)

const emit = defineEmits<{
  click: [event: MouseEvent]
  'update:offset': [offset: FloatingBubbleOffset]
  offsetChange: [offset: FloatingBubbleOffset]
}>()

const rootRef = ref<HTMLDivElement>()

const state = ref({
  x: 0,
  y: 0,
  width: 0,
  height: 0
})

const { width: windowWidth, height: windowHeight } = useWindowSize()

const gapX = computed(() => (isObject(props.gap) ? props.gap.x : props.gap))
const gapY = computed(() => (isObject(props.gap) ? props.gap.y : props.gap))

const boundary = computed<FloatingBubbleBoundary>(() => ({
  top: gapY.value,
  right: windowWidth.value - state.value.width - gapX.value,
  bottom: windowHeight.value - state.value.height - gapY.value,
  left: gapX.value
}))

const dragging = ref(false)
let initialized = false

const clamp = (value: number, min: number, max: number) => {
  if (max < min) return min
  return Math.min(Math.max(value, min), max)
}

const rootStyle = computed(() => {
  const style: CSSProperties = {}

  const x = addUnit(state.value.x)
  const y = addUnit(state.value.y)
  style.transform = `translate3d(${x}, ${y}, 0)`

  if (dragging.value || !initialized) {
    style.transition = 'none'
  }

  return style
})

const show = ref(true)

const updateState = () => {
  if (!show.value || !rootRef.value) return

  const rect = rootRef.value.getBoundingClientRect()
  const { offset } = props
  const nextBoundary = {
    top: gapY.value,
    right: windowWidth.value - rect.width - gapX.value,
    bottom: windowHeight.value - rect.height - gapY.value,
    left: gapX.value
  }

  state.value = {
    x: clamp(offset ? offset.x : nextBoundary.right, nextBoundary.left, nextBoundary.right),
    y: clamp(offset ? offset.y : nextBoundary.bottom, nextBoundary.top, nextBoundary.bottom),
    width: rect.width,
    height: rect.height
  }
}

// Touch support
let prevX = 0
let prevY = 0
let isTouchMove = false
let startTouchX = 0
let startTouchY = 0

const onTouchStart = (e: TouchEvent) => {
  const touch = e.touches[0]
  if (!touch) return

  dragging.value = true
  isTouchMove = false
  startTouchX = touch.clientX
  startTouchY = touch.clientY

  prevX = state.value.x
  prevY = state.value.y
}

const onTouchEnd = () => {
  dragging.value = false

  nextTick(() => {
    if (isTouchMove) {
      if (props.magnetic === 'x') {
        const nextX = closest([boundary.value.left, boundary.value.right], state.value.x)
        state.value.x = nextX
      }
      if (props.magnetic === 'y') {
        const nextY = closest([boundary.value.top, boundary.value.bottom], state.value.y)
        state.value.y = nextY
      }

      const offset = pick(state.value, ['x', 'y'])
      emit('update:offset', offset)
      if (prevX !== offset.x || prevY !== offset.y) {
        emit('offsetChange', offset)
      }
    }
  })
}

// Mouse support for desktop
let isMouseDown = false
let startMouseX = 0
let startMouseY = 0

const onMouseDown = (e: MouseEvent) => {
  isMouseDown = true
  dragging.value = true
  isTouchMove = false

  startMouseX = e.clientX
  startMouseY = e.clientY
  prevX = state.value.x
  prevY = state.value.y

  e.preventDefault()
}

const onMouseMove = (e: MouseEvent) => {
  if (!isMouseDown) return

  e.preventDefault()
  isTouchMove = true

  if (props.axis === 'lock') return

  const deltaX = e.clientX - startMouseX
  const deltaY = e.clientY - startMouseY

  if (props.axis === 'x' || props.axis === 'xy') {
    let nextX = prevX + deltaX
    if (nextX < boundary.value.left) nextX = boundary.value.left
    if (nextX > boundary.value.right) nextX = boundary.value.right
    state.value.x = nextX
  }

  if (props.axis === 'y' || props.axis === 'xy') {
    let nextY = prevY + deltaY
    if (nextY < boundary.value.top) nextY = boundary.value.top
    if (nextY > boundary.value.bottom) nextY = boundary.value.bottom
    state.value.y = nextY
  }

  const offset = pick(state.value, ['x', 'y'])
  emit('update:offset', offset)
}

const onMouseUp = () => {
  if (!isMouseDown) return

  isMouseDown = false
  dragging.value = false

  nextTick(() => {
    if (isTouchMove) {
      if (props.magnetic === 'x') {
        const nextX = closest([boundary.value.left, boundary.value.right], state.value.x)
        state.value.x = nextX
      }
      if (props.magnetic === 'y') {
        const nextY = closest([boundary.value.top, boundary.value.bottom], state.value.y)
        state.value.y = nextY
      }

      const offset = pick(state.value, ['x', 'y'])
      emit('update:offset', offset)
      if (prevX !== offset.x || prevY !== offset.y) {
        emit('offsetChange', offset)
      }
    }
  })
}

const onClick = (e: MouseEvent) => {
  if (!isTouchMove) {
    emit('click', e)
  } else {
    e.stopPropagation()
  }
}

onMounted(() => {
  updateState()
  nextTick(() => {
    initialized = true
  })
})

watch([windowWidth, windowHeight, gapX, gapY, () => props.offset], updateState, { deep: true })
</script>
